/**
 * Niche v3 — design-token mapping for visual gradients.
 *
 * Parallel pattern: `src/lib/metric-styles.ts` — единый конфиг,
 * никогда не хардкодить hex/className в компонентах
 * (project rule `.claude/rules/shared-styles.md`).
 *
 * Anti-pattern избегаем (см. research/ux/niche/concept-v3.md §7 AP-2):
 * НЕ рандомная ротация градиентов по index. Accent = decision от Seeker,
 * informed by viability (mood ∈ hot/stable/frontier).
 *
 * Client-safe: нет pg / DB импортов. Importable в React components.
 *
 * NOTE — sibling module `src/lib/tier-tokens.ts` covers the *post-debate
 * grade tier* surface (emerald/amber/red keyed off `getScoreGrade().color`)
 * for OG / share-card rendering with inline `rgba()` styles. That is a
 * different domain (grade, not mood) on a different render target (Satori,
 * not React/Tailwind), so the two maps stay independent — see BILL-05.
 */

import type {
  NicheAccentToken,
  NicheVisualMood,
} from "@/types/niche-v3";

// =====================================================================
// 1. Token descriptor shape
// =====================================================================

/**
 * Per-token design descriptor.
 * Scene consumers resolve this via NICHE_ACCENT_TOKENS[accent].
 */
export interface NicheAccentDescriptor {
  /** Tailwind gradient class (for hero-art zones, foil badges). */
  gradientClassName: string;
  /** Tailwind text-color class (for accented headlines / chips). */
  textClassName: string;
  /** Tailwind ring/border class (for focus rings + card emphasis). */
  ringClassName: string;
  /** Tailwind shadow class (для foil glow / grade-badge halo). */
  shadowClassName: string;
  /** Light-mode primary hex (screenshot/Satori export). */
  hex: string;
  /** Dark-mode primary hex (Satori share-cards rendered на dark bg). */
  hexDark: string;
}

// =====================================================================
// 2. Canonical token map
// =====================================================================

/**
 * Five accent tokens — decision-driven by `visualGradient.accent`.
 *
 * Color semantics:
 *   - warm-amber    — hot / urgency / dawn. Default for mood=hot.
 *   - cool-emerald  — proven / steady / growth. Default for mood=stable.
 *   - electric-lime — frontier / first-mover / new tech. Default for mood=frontier.
 *   - deep-violet   — premium / proven-with-depth. Alt для stable.
 *   - burnt-orange  — hot-aggressive / high-urgency. Alt для hot.
 *
 * Gradients все идут amber-300 → orange-500 style (2-stop bg-gradient-to-br).
 * Tailwind classes использую JIT-friendly patterns (explicit не compute'нутые).
 */
const NICHE_ACCENT_TOKENS: Record<
  NicheAccentToken,
  NicheAccentDescriptor
> = {
  "warm-amber": {
    // amber-300 → orange-500
    gradientClassName: "bg-gradient-to-br from-amber-300 to-orange-500",
    textClassName: "text-amber-400",
    ringClassName: "ring-amber-400/60",
    shadowClassName: "shadow-amber-500/40",
    hex: "#fcd34d", // amber-300
    hexDark: "#f97316", // orange-500
  },
  "cool-emerald": {
    // emerald-400 → teal-600
    gradientClassName: "bg-gradient-to-br from-emerald-400 to-teal-600",
    textClassName: "text-emerald-400",
    ringClassName: "ring-emerald-400/60",
    shadowClassName: "shadow-emerald-500/40",
    hex: "#34d399", // emerald-400
    hexDark: "#0d9488", // teal-600
  },
  "electric-lime": {
    // lime-400 → green-500
    gradientClassName: "bg-gradient-to-br from-lime-400 to-green-500",
    textClassName: "text-lime-400",
    ringClassName: "ring-lime-400/60",
    shadowClassName: "shadow-lime-500/40",
    hex: "#a3e635", // lime-400
    hexDark: "#22c55e", // green-500
  },
  "deep-violet": {
    // violet-500 → purple-700
    gradientClassName: "bg-gradient-to-br from-violet-500 to-purple-700",
    textClassName: "text-violet-400",
    ringClassName: "ring-violet-400/60",
    shadowClassName: "shadow-violet-500/40",
    hex: "#8b5cf6", // violet-500
    hexDark: "#7e22ce", // purple-700
  },
  "burnt-orange": {
    // orange-400 → red-600
    gradientClassName: "bg-gradient-to-br from-orange-400 to-red-600",
    textClassName: "text-orange-400",
    ringClassName: "ring-orange-400/60",
    shadowClassName: "shadow-orange-500/40",
    hex: "#fb923c", // orange-400
    hexDark: "#dc2626", // red-600
  },
};

// =====================================================================
// 3. Mood → default accent mapping
// =====================================================================

/**
 * Default accent per mood, used если Seeker не указал explicit `accent`
 * или указал invalid token (guarded elsewhere).
 *
 * Mapping по concept-v3.md §3 DM-1:
 *   hot     → warm-amber      (energetic, dawn-like)
 *   stable  → cool-emerald    (proven, growth)
 *   frontier → electric-lime  (first-mover, bright)
 */
function getMoodAccentDefault(
  mood: NicheVisualMood,
): NicheAccentToken {
  switch (mood) {
    case "hot":
      return "warm-amber";
    case "stable":
      return "cool-emerald";
    case "frontier":
      return "electric-lime";
  }
}

/**
 * Convenience: get gradient class for a mood (uses default accent).
 * Used by hero-art zones on Grid cards when AI didn't ship explicit accent.
 */
export function getMoodGradient(mood: NicheVisualMood): string {
  const accent = getMoodAccentDefault(mood);
  return NICHE_ACCENT_TOKENS[accent].gradientClassName;
}

/**
 * Convenience: resolve a (possibly-unsafe) accent string to full descriptor.
 * Falls back to mood's default if accent unknown.
 */
export function resolveAccent(
  accent: NicheAccentToken | string | undefined,
  fallbackMood: NicheVisualMood = "stable",
): NicheAccentDescriptor {
  if (accent && accent in NICHE_ACCENT_TOKENS) {
    return NICHE_ACCENT_TOKENS[accent as NicheAccentToken];
  }
  return NICHE_ACCENT_TOKENS[getMoodAccentDefault(fallbackMood)];
}

// =====================================================================
// 4. USD amount parser (reused by market-waterfall validators)
// =====================================================================

/**
 * Parse USD display strings → number.
 *
 * Supports:
 *   - "$4.2B"       → 4_200_000_000
 *   - "$47k/mo"     → 47_000
 *   - "$1.5M"       → 1_500_000
 *   - "$340"        → 340
 *   - "47000"       → 47000
 *   - "$8-80K MRR"  → 80_000 (range → MAX / upper bound)
 *
 * Strips: "$" prefix, "/mo" / "/yr" / "MRR" / "ARR" suffixes, whitespace.
 * Returns: non-negative number, or null if no digit found.
 *
 * Range semantics: для строк вида "$8-80K" возвращаем **верхнюю границу** —
 * mass-tier Foundry sells the dream (memory `feedback_dream_vs_outcome.md`),
 * UI hero numbers — aspirational ceiling, не conservative floor. Mirrors
 * `extractHeroMoney()` in `src/lib/share-card/niche-tokens.ts:76-87`.
 *
 * WHY не библиотека: нестандартные суффиксы ("MRR", "/mo") + range-handling
 * делают библиотечный parseInt недостаточным. 20 строк locally — ок.
 */
export function parseUSDAmount(str: string): number | null {
  if (typeof str !== "string") return null;

  // Strip common suffixes/prefixes. Keep digits, optional decimal, KMB suffix.
  const cleaned = str
    .replace(/\$/g, "")
    .replace(/,/g, "")
    .replace(/\/(mo|yr|month|year)\b/gi, "")
    .replace(/\b(MRR|ARR|revenue|mrr|arr)\b/gi, "")
    .trim();

  // Match first number with optional decimal + optional range upper bound +
  // optional K/M/B suffix. For ranges like "8-80K": pick the upper bound ("80")
  // and apply suffix ("K") — aspirational ceiling, dream-selling brand voice.
  const match = cleaned.match(
    /(\d+(?:\.\d+)?)(?:\s*-\s*(\d+(?:\.\d+)?))?\s*([KMB])?/i,
  );
  if (!match) return null;

  // match[2] present → range; pick upper bound. Else → single number (match[1]).
  const rawValue = match[2] ?? match[1];
  const value = parseFloat(rawValue);
  if (Number.isNaN(value)) return null;

  const suffix = (match[3] || "").toUpperCase();
  const multiplier =
    suffix === "B" ? 1_000_000_000
    : suffix === "M" ? 1_000_000
    : suffix === "K" ? 1_000
    : 1;

  return value * multiplier;
}
